import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import ChevronDown from 'lucide-react/dist/esm/icons/chevron-down';
import ChevronUp from 'lucide-react/dist/esm/icons/chevron-up';
import Sparkles from 'lucide-react/dist/esm/icons/sparkles';
import Users from 'lucide-react/dist/esm/icons/users';
import Check from 'lucide-react/dist/esm/icons/check';
import Clock from 'lucide-react/dist/esm/icons/clock';
import MessageSquare from 'lucide-react/dist/esm/icons/message-square';
import Quote from 'lucide-react/dist/esm/icons/quote';
import Lightbulb from 'lucide-react/dist/esm/icons/lightbulb';
import TrendingUp from 'lucide-react/dist/esm/icons/trending-up';
import Search from 'lucide-react/dist/esm/icons/search';
import Download from 'lucide-react/dist/esm/icons/download';
import RefreshCw from 'lucide-react/dist/esm/icons/refresh-cw';
import { useSurveyStore } from '../../stores/surveyStore';
import { useUIStore } from '../../stores/uiStore';
import { getTranslation } from '../../i18n';
import { surveyApi } from '../../services/api/survey';
import { getCachedLocalStorage } from '../../utils/storage';
import { Text, Badge, Spinner, Tag, Button } from '../../components/ui';
import type { RecipientResult, AiSurveySummary, AiQuestionSummary } from './types';
import { resolveML } from './types';

type ResultTab = 'ai' | 'responses';

type TFunc = (key: string) => string;

const SurveyResults: React.FC = () => {
  const { activeSurvey, activeResults, loadResults } = useSurveyStore();
  const { language } = useUIStore();
  const t: TFunc = (key: string) => getTranslation(language, key);

  const [tab, setTab] = useState<ResultTab>('ai');
  const [aiData, setAiData] = useState<AiSurveySummary | null>(null);
  const [aiRaw, setAiRaw] = useState<string | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedRecipient, setSelectedRecipient] = useState<string | null>(null);
  const [emailListExpanded, setEmailListExpanded] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (activeSurvey) {
      loadResults(activeSurvey.id);

      const raw = activeSurvey.ai_summary;
      if (raw) {
        tryParseAiSummary(raw);
      } else {
        setAiLoading(true);
        surveyApi.getAiSummary(activeSurvey.id)
          .then(({ data }) => tryParseAiSummary(data.ai_summary))
          .catch(() => { setAiData(null); setAiRaw(null); })
          .finally(() => setAiLoading(false));
      }
    }
  }, [activeSurvey, loadResults]);

  const tryParseAiSummary = useCallback((raw: string) => {
    try {
      const parsed = JSON.parse(raw) as AiSurveySummary;
      if (parsed.overview && parsed.question_summaries) {
        setAiData(parsed);
        setAiRaw(null);
      } else {
        setAiData(null);
        setAiRaw(raw);
      }
    } catch {
      setAiData(null);
      setAiRaw(raw);
    }
  }, []);

  const [regenerating, setRegenerating] = useState(false);

  const handleRegenerate = useCallback(async () => {
    if (!activeSurvey) return;
    setRegenerating(true);
    setAiData(null);
    setAiRaw(null);
    try {
      const { data } = await surveyApi.regenerateAiSummary(activeSurvey.id);
      tryParseAiSummary(data.ai_summary);
    } catch {
      setAiData(null);
      setAiRaw(null);
    } finally {
      setRegenerating(false);
    }
  }, [activeSurvey, tryParseAiSummary]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const allRecipients = activeResults?.recipients || [];

  const autocompleteResults = useMemo(() => {
    if (!searchQuery.trim()) return allRecipients;
    const q = searchQuery.toLowerCase();
    return allRecipients.filter((r) =>
      r.email.toLowerCase().includes(q) || (r.name && r.name.toLowerCase().includes(q)),
    );
  }, [allRecipients, searchQuery]);

  const selectedRecipientData = useMemo(
    () => allRecipients.find((r) => r.recipient_id === selectedRecipient) || null,
    [allRecipients, selectedRecipient],
  );

  const handleSelectRecipient = useCallback((id: string) => {
    setSelectedRecipient((prev) => (prev === id ? null : id));
    setShowDropdown(false);
    setSearchQuery('');
  }, []);

  const [downloading, setDownloading] = useState(false);
  const handleDownloadPdf = useCallback(async () => {
    if (!activeSurvey) return;
    setDownloading(true);
    try {
      const url = surveyApi.getExportPdfUrl(activeSurvey.id, language);
      const token = getCachedLocalStorage('quazi_access_token');
      const res = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `quazi_survey_${(activeSurvey.title || 'report').replace(/\s+/g, '_').slice(0, 40)}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      // silent fail — could add toast here
    } finally {
      setDownloading(false);
    }
  }, [activeSurvey, language]);

  if (!activeSurvey) return null;

  return (
    <div className="h-full flex flex-col">
      {/* Sub-tabs */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-subtle bg-surface-page">
        <div className="flex items-center gap-1">
          <button
            onClick={() => setTab('ai')}
            className={`flex items-center gap-2 px-4 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${
              tab === 'ai'
                ? 'bg-brand/15 text-primary shadow-sm'
                : 'text-content-tertiary hover:text-content-secondary hover:bg-surface-primary'
            }`}
          >
            <Sparkles size={14} />
            {t('surveyResultsTabAiSummary')}
          </button>
          <button
            onClick={() => setTab('responses')}
            className={`flex items-center gap-2 px-4 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${
              tab === 'responses'
                ? 'bg-brand/15 text-primary shadow-sm'
                : 'text-content-tertiary hover:text-content-secondary hover:bg-surface-primary'
            }`}
          >
            <Users size={14} />
            {t('surveyResultsTabResponses')}
            {allRecipients.length > 0 && (
              <span className="text-[10px] text-content-muted">
                ({allRecipients.filter((r) => r.status === 'completed').length}/{allRecipients.length})
              </span>
            )}
          </button>
        </div>
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<Download size={14} />}
          onClick={handleDownloadPdf}
          loading={downloading}
        >
          {t('surveyResultsBtnDownloadPdf')}
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* ── AI SUMMARY TAB ── */}
        {tab === 'ai' && (
          <div className="max-w-3xl mx-auto px-6 py-8">
            {!aiLoading && !regenerating && (
              <div className="flex justify-end mb-4">
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={<RefreshCw size={13} />}
                  onClick={handleRegenerate}
                >
                  {t('surveyResultsBtnRegenerate')}
                </Button>
              </div>
            )}
            {aiLoading || regenerating ? (
              <div className="flex flex-col items-center justify-center h-64 gap-3">
                <Spinner size="lg" />
                <Text variant="body" className="text-content-muted">{t('surveyResultsAiLoading')}</Text>
              </div>
            ) : aiData ? (
              <StructuredSummary data={aiData} t={t} lang={language} />
            ) : aiRaw ? (
              <FallbackSummary text={aiRaw} t={t} />
            ) : (
              <div className="flex flex-col items-center justify-center h-64 text-content-muted">
                <Sparkles size={40} className="mb-4 opacity-30" />
                <Text variant="body">{t('surveyResultsAiNotAvailable')}</Text>
              </div>
            )}
          </div>
        )}

        {/* ── RESPONSES TAB ── */}
        {tab === 'responses' && (
          <div className="max-w-3xl mx-auto px-6 py-6 space-y-4">
            {/* Search with autocomplete */}
            <div ref={searchRef} className="relative">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-content-muted" />
                <input
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setShowDropdown(true); }}
                  onFocus={() => setShowDropdown(true)}
                  placeholder={t('surveyResultsSearchPlaceholder')}
                  className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-surface-primary border border-subtle text-content text-xs placeholder-content-muted outline-none focus:border-brand/40 transition-all"
                />
              </div>

              {showDropdown && searchQuery.trim() && autocompleteResults.length > 0 && (
                <div className="absolute z-dropdown mt-1 w-full max-h-48 overflow-y-auto rounded-xl bg-surface-page border border-default shadow-lg">
                  {autocompleteResults.map((r) => (
                    <button
                      key={r.recipient_id}
                      onClick={() => handleSelectRecipient(r.recipient_id)}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm hover:bg-surface-secondary transition-all ${
                        selectedRecipient === r.recipient_id ? 'bg-brand/10' : ''
                      }`}
                    >
                      <StatusDot completed={r.status === 'completed'} />
                      <div className="flex-1 min-w-0">
                        <span className="text-content font-medium truncate block">{r.name || r.email}</span>
                        {r.name && <span className="text-[10px] text-content-muted truncate block">{r.email}</span>}
                      </div>
                      {r.source === 'link' && <Tag label={t('surveyResultsTagViaLink')} variant="brand" />}
                      <Badge variant={r.status === 'completed' ? 'success' : 'neutral'} size="sm">
                        {r.status === 'completed' ? t('surveyResultsBadgeCompleted') : t('surveyResultsBadgePending')}
                      </Badge>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Collapsible email list */}
            <div className="rounded-xl bg-surface-primary border border-subtle overflow-hidden">
              <button
                onClick={() => setEmailListExpanded((v) => !v)}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-surface-secondary transition-all"
              >
                <div className="flex items-center gap-2">
                  <Users size={14} className="text-content-muted" />
                  <Text variant="label">
                    {t('surveyResultsAllRecipients')
                      .replace('{{responded}}', String(allRecipients.filter((r) => r.status === 'completed').length))
                      .replace('{{total}}', String(allRecipients.length))}
                  </Text>
                </div>
                {emailListExpanded
                  ? <ChevronUp size={16} className="text-content-muted" />
                  : <ChevronDown size={16} className="text-content-muted" />
                }
              </button>

              {emailListExpanded && (
                <div className="border-t border-subtle max-h-64 overflow-y-auto">
                  {allRecipients.map((r) => (
                    <button
                      key={r.recipient_id}
                      onClick={() => handleSelectRecipient(r.recipient_id)}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-surface-secondary transition-all border-b border-subtle last:border-b-0 ${
                        selectedRecipient === r.recipient_id ? 'bg-brand/5 border-l-2 border-l-brand' : ''
                      }`}
                    >
                      <StatusDot completed={r.status === 'completed'} />
                      <div className="flex-1 min-w-0">
                        <span className="text-sm text-content font-medium truncate block">{r.name || r.email}</span>
                        {r.name && <span className="text-[10px] text-content-muted truncate block">{r.email}</span>}
                      </div>
                      {r.source === 'link' && <Tag label={t('surveyResultsTagViaLink')} variant="brand" />}
                      <Badge variant={r.status === 'completed' ? 'success' : 'neutral'} size="sm">
                        {r.status === 'completed' ? t('surveyResultsBadgeCompleted') : t('surveyResultsBadgePending')}
                      </Badge>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Selected recipient answers */}
            {selectedRecipientData ? (
              <div className="space-y-1">
                <div className="flex items-center gap-2 mb-4 flex-wrap">
                  <Text variant="subheading">{selectedRecipientData.name || selectedRecipientData.email}</Text>
                  {selectedRecipientData.name && (
                    <Text variant="caption" className="text-content-muted">{selectedRecipientData.email}</Text>
                  )}
                  <Badge variant={selectedRecipientData.status === 'completed' ? 'success' : 'neutral'} size="sm">
                    {selectedRecipientData.status === 'completed' ? t('surveyResultsBadgeCompleted') : t('surveyResultsBadgePending')}
                  </Badge>
                  {selectedRecipientData.source === 'link' && <Tag label={t('surveyResultsTagViaLink')} variant="brand" />}
                </div>

                {selectedRecipientData.status !== 'completed' ? (
                  <div className="flex flex-col items-center justify-center h-32 text-content-muted rounded-xl bg-surface-primary border border-subtle">
                    <Clock size={24} className="mb-2 opacity-40" />
                    <Text variant="body">{t('surveyResultsNotRespondedYet')}</Text>
                  </div>
                ) : selectedRecipientData.answers.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-32 text-content-muted rounded-xl bg-surface-primary border border-subtle">
                    <Text variant="body">{t('surveyResultsNoAnswers')}</Text>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {selectedRecipientData.answers.map((answer, idx) => (
                      <div key={answer.id} className="rounded-xl bg-surface-primary border border-subtle p-4 space-y-2">
                        <div className="flex items-start gap-2">
                          <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-md bg-surface-secondary text-content-muted text-[11px] font-medium">
                            {idx + 1}
                          </span>
                          <Text variant="label" className="text-content pt-0.5">
                            {answer.question_text || t('surveyResultsFallbackQuestion')}
                          </Text>
                        </div>
                        <div className="ml-8 px-3 py-2.5 rounded-lg bg-surface-secondary text-sm text-content leading-relaxed">
                          {answer.answer_text}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-32 text-content-muted">
                <MessageSquare size={24} className="mb-2 opacity-30" />
                <Text variant="body">{t('surveyResultsSelectRecipient')}</Text>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Helper: status dot ──
const StatusDot: React.FC<{ completed: boolean }> = ({ completed }) => (
  <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
    completed ? 'bg-emerald-400/10' : 'bg-surface-secondary'
  }`}>
    {completed
      ? <Check size={10} className="text-emerald-400" />
      : <Clock size={10} className="text-content-muted" />
    }
  </div>
);

// ── Structured AI Summary ──
const StructuredSummary: React.FC<{ data: AiSurveySummary; t: TFunc; lang: string }> = ({ data, t, lang }) => {
  const overview = resolveML(data.overview, lang);
  const takeaways = resolveML(data.takeaways, lang);

  return (
    <div className="space-y-6">
      {/* Overview card */}
      <div className="rounded-xl bg-gradient-to-br from-brand/5 to-transparent border border-brand/15 p-5">
        <div className="flex items-start gap-3 mb-3">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-brand/10 flex-shrink-0">
            <Sparkles size={16} className="text-primary" />
          </div>
          <div>
            <Text variant="subheading" className="text-content mb-1">{t('surveyResultsOverview')}</Text>
            <Text variant="body" className="text-content-secondary leading-relaxed">{overview}</Text>
          </div>
        </div>
        <div className="ml-11">
          <Badge variant="info" size="sm">
            {data.total_responses !== 1
              ? t('surveyResultsResponseCountPlural').replace('{{count}}', String(data.total_responses))
              : t('surveyResultsResponseCount').replace('{{count}}', String(data.total_responses))}
          </Badge>
        </div>
      </div>

      {/* Per-question summaries */}
      {data.question_summaries.map((qs, idx) => (
        <QuestionSummaryCard key={idx} index={idx} data={qs} t={t} lang={lang} />
      ))}

      {/* Takeaways */}
      {takeaways.length > 0 && (
        <div className="rounded-xl bg-surface-primary border border-subtle p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-amber-400/10 flex-shrink-0">
              <Lightbulb size={14} className="text-amber-400" />
            </div>
            <Text variant="subheading" className="text-content">{t('surveyResultsKeyTakeaways')}</Text>
          </div>
          <div className="space-y-2.5 ml-9">
            {takeaways.map((item, i) => (
              <div key={i} className="flex items-start gap-2.5">
                <TrendingUp size={14} className="text-emerald-400 flex-shrink-0 mt-0.5" />
                <Text variant="body" className="text-content-secondary leading-relaxed">{item}</Text>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Question summary card ──
const QuestionSummaryCard: React.FC<{ index: number; data: AiQuestionSummary; t: TFunc; lang: string }> = ({ index, data, t, lang }) => {
  const summary = resolveML(data.summary, lang);
  const keyThemes = resolveML(data.key_themes, lang);

  const SENTIMENT_MAP: Record<string, { variant: 'success' | 'warning' | 'danger' | 'info' | 'neutral'; label: string }> = {
    positive: { variant: 'success', label: t('surveyResultsSentimentPositive') },
    negative: { variant: 'danger', label: t('surveyResultsSentimentNegative') },
    mixed: { variant: 'warning', label: t('surveyResultsSentimentMixed') },
    neutral: { variant: 'neutral', label: t('surveyResultsSentimentNeutral') },
  };
  const sentiment = SENTIMENT_MAP[data.sentiment] || SENTIMENT_MAP.neutral;

  return (
    <div className="rounded-xl bg-surface-primary border border-subtle overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-subtle">
        <div className="flex items-start gap-3">
          <span className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-lg bg-brand/10 text-primary text-xs font-semibold">
            {index + 1}
          </span>
          <div className="flex-1 min-w-0">
            <Text variant="body" className="text-content font-medium">{data.question}</Text>
          </div>
          <Badge variant={sentiment.variant} size="sm" dot>{sentiment.label}</Badge>
        </div>
      </div>

      {/* Summary */}
      <div className="px-5 py-4 space-y-4">
        <Text variant="body" className="text-content-secondary leading-relaxed">{summary}</Text>

        {/* Key themes */}
        {keyThemes.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {keyThemes.map((theme, i) => (
              <Tag key={i} label={theme} variant="brand" />
            ))}
          </div>
        )}

        {/* Notable quotes */}
        {data.notable_quotes.length > 0 && (
          <div className="space-y-2 pt-1">
            {data.notable_quotes.map((q, i) => (
              <div key={i} className="flex items-start gap-2.5 pl-1">
                <Quote size={12} className="text-content-muted flex-shrink-0 mt-1" />
                <Text variant="caption" className="text-content-muted italic leading-relaxed">"{q}"</Text>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Fallback for non-JSON (legacy) summaries ──
const FallbackSummary: React.FC<{ text: string; t: TFunc }> = ({ text, t }) => (
  <div className="rounded-xl bg-surface-primary border border-subtle p-6">
    <div className="flex items-center gap-2 mb-4">
      <Sparkles size={16} className="text-primary" />
      <Text variant="subheading" className="text-content">{t('surveyResultsFallbackTitle')}</Text>
    </div>
    <div className="text-sm text-content-secondary leading-relaxed whitespace-pre-wrap">
      {text}
    </div>
  </div>
);

export default React.memo(SurveyResults);
